import React, { FC } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { IField } from '../Field/Field';
import Player from '../Player/Player';

import BoardSquare from '../BoardSquare/BoardSquare';

import { IPlayer } from '../../../../shared/types/player.type';
import styled from 'styled-components';
import { PlayerEnum } from '../Player/constants';

interface IBoard {
  tokenPositions: IPlayer[];
  changes: {
    text: string;
    from: [number, number] | [null, null];
    to: [number, number] | [null, null];
  }[];
  startRef: any;
  endRef: any;
  selectedPlayer: number | null;
}

interface RenderField {
  x: number;
  y: number;
  tokenPositions: IPlayer[];
  innerRef: any;
  selectedPlayer: number | null;
}

const StyledArrow = styled.img.attrs(() => ({
  src: './arrow.svg',
  alt: 'arrow',
}))`
  width: 10px;
  position: absolute;
`;

const StyledBottomArrow = styled(StyledArrow)`
  left: 220px;
  top: 557px;
`;

const StyledRightArrow = styled(StyledArrow)`
  transform: rotate(270deg);
  left: 576px;
  top: 390px;
`;
const StyledTopArrow = styled(StyledArrow)`
  transform: rotate(180deg);
  left: 410px;
  top: 30px;
`;
const StyledLeftArrow = styled(StyledArrow)`
  transform: rotate(90deg);
  left: 50px;
  top: 200px;
`;

export let yardFields = [
  [
    [0, 0],
    [0, 1],
    [1, 0],
    [1, 1],
  ],
  [
    [9, 0],
    [9, 1],
    [10, 0],
    [10, 1],
  ],
  [
    [9, 9],
    [9, 10],
    [10, 9],
    [10, 10],
  ],
  [
    [0, 9],
    [0, 10],
    [1, 9],
    [1, 10],
  ],
];

function renderField(props: RenderField) {
  const { selectedPlayer } = props;
  if (!props.tokenPositions) {
    return null;
  }

  const player = props.tokenPositions.find(
    (currentPlayer) =>
      currentPlayer.position[0] === props.x &&
      currentPlayer.position[1] === props.y,
  );

  function isOfField(fields: number[][]) {
    return fields.some((arr) => arr[0] === props.x && arr[1] === props.y);
  }

  function isField(field: number[]) {
    return field[0] === props.x && field[1] === props.y;
  }

  let startFields = [
    [0, 4],
    [6, 0],
    [10, 6],
    [4, 10],
  ];

  let homeFields = [
    [
      [1, 5],
      [2, 5],
      [3, 5],
      [4, 5],
    ],
    [
      [5, 1],
      [5, 2],
      [5, 3],
      [5, 4],
    ],
    [
      [6, 5],
      [7, 5],
      [8, 5],
      [9, 5],
    ],
    [
      [5, 6],
      [5, 7],
      [5, 8],
      [5, 9],
    ],
  ];

  let emptyFields = [
    [5, 5],
    [2, 0],
    [3, 0],
    [7, 0],
    [8, 0],
    [2, 1],
    [3, 1],
    [7, 1],
    [8, 1],
    [2, 9],
    [3, 9],
    [7, 9],
    [8, 9],
    [2, 10],
    [3, 10],
    [7, 10],
    [8, 10],
    [0, 2],
    [1, 2],
    [2, 2],
    [3, 2],
    [7, 2],
    [8, 2],
    [9, 2],
    [10, 2],
    [0, 3],
    [1, 3],
    [2, 3],
    [3, 3],
    [7, 3],
    [8, 3],
    [9, 3],
    [10, 3],
    [0, 7],
    [1, 7],
    [2, 7],
    [3, 7],
    [7, 7],
    [8, 7],
    [9, 7],
    [10, 7],
    [0, 8],
    [1, 8],
    [2, 8],
    [3, 8],
    [7, 8],
    [8, 8],
    [9, 8],
    [10, 8],
  ];

  const isEmpty = emptyFields.some(
    (arr) => arr[0] === props.x && arr[1] === props.y,
  );

  let owner: IField['owner'] = 'community';

  if (
    isOfField(yardFields[0]) ||
    isOfField(homeFields[0]) ||
    isField(startFields[0])
  ) {
    owner = PlayerEnum.ONE;
  } else if (
    isOfField(yardFields[1]) ||
    isOfField(homeFields[1]) ||
    isField(startFields[1])
  ) {
    owner = PlayerEnum.TWO;
  } else if (
    isOfField(yardFields[2]) ||
    isOfField(homeFields[2]) ||
    isField(startFields[2])
  ) {
    owner = PlayerEnum.THREE;
  } else if (
    isOfField(yardFields[3]) ||
    isOfField(homeFields[3]) ||
    isField(startFields[3])
  ) {
    owner = PlayerEnum.FOUR;
  }

  const Piece = () =>
    player ? <Player number={player.number} id={player.id} /> : null;

  const isAccessible =
    !isOfField(yardFields[0]) &&
    !isOfField(yardFields[1]) &&
    !isOfField(yardFields[2]) &&
    !isOfField(yardFields[3]) &&
    !homeFields
      .map((homeField, index) => {
        if (selectedPlayer === null) return false;
        if (index !== selectedPlayer) {
          if (isOfField(homeField)) return true;
        }
        return false;
      })
      .includes(true);

  return (
    <div>
      <BoardSquare
        innerRef={props.innerRef}
        id={props.y * 11 + props.x}
        x={props.x}
        y={props.y}
        owner={owner}
        isEmpty={isEmpty}
        isAccessible={isAccessible}
        selectedPlayer={selectedPlayer}
      >
        <Piece />
      </BoardSquare>
    </div>
  );
}

const StyledBoard = styled.div`
  display: flex;
  border: solid red 10px;
  width: 620px;
`;
const Board: FC<IBoard> = (props) => {
  const { selectedPlayer } = props;

  const adjustPosition = (x: number, y: number) => {
    if (selectedPlayer === null) return { x, y };

    switch (selectedPlayer) {
      case 0:
        return { x: 10 - y, y: x };
      case 1:
        return { x: 10 - x, y: 10 - y };
      case 2:
        return { x: y, y: 10 - x };
      case 3:
        return { x, y };
      default:
        return { x, y };
    }
  };

  const fields = [];
  let indexOfLastMove: number | null = null;
  for (let i = props.changes.length - 1; i >= 0; i--) {
    if (props.changes[i].from[0] !== null) {
      indexOfLastMove = i;
      break;
    }
  }

  for (let x = 0; x < 11; x++) {
    fields.push(
      <div>
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((y) => {
          const adjustedPosition = adjustPosition(x, y);
          let innerRef = null;
          if (
            indexOfLastMove !== null &&
            props.changes[indexOfLastMove].from[0] === adjustedPosition.x &&
            props.changes[indexOfLastMove].from[1] === adjustedPosition.y
          ) {
            innerRef = props.startRef;
          }
          if (
            indexOfLastMove !== null &&
            props.changes[indexOfLastMove].to[0] === adjustedPosition.x &&
            props.changes[indexOfLastMove].to[1] === adjustedPosition.y
          ) {
            innerRef = props.endRef;
          }
          return renderField({
            x: adjustedPosition.x,
            y: adjustedPosition.y,
            tokenPositions: props.tokenPositions,
            innerRef: innerRef,
            selectedPlayer: selectedPlayer,
          });
        })}
      </div>,
    );
  }
  return (
    <DndProvider backend={HTML5Backend}>
      <StyledBoard>
        {fields}
        <StyledBottomArrow />
        <StyledRightArrow />
        <StyledTopArrow />
        <StyledLeftArrow />
      </StyledBoard>
    </DndProvider>
  );
};

export default Board;
